import { faEye } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Dialog, DialogContent, Grid, Typography } from "@mui/material";
import { isEqual } from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TravelExpenseDTO } from "src/models/travel-expenses/travel-expense-dto";
import { v4 as uuidv4 } from "uuid";
import EmployeeActions from "./EmployeeActions/EmployeeActions";
import ExportSingleExpenseReport from "./ExportSingleExpenseReport/ExportSingleExpenseReport";
import ManagerActions from "./ManagerActions/ManagerActions";
import ManagerConfirmationDialog from "./ManagerActions/ManagerConfirmationDialog/ManagerConfirmationDialog";
import ConfirmationChangesDialog from "../../../../containers/ConfirmationChangesDialog/ConfirmationChangesDialog";
import CustomConfirmationChangesDialog from "../../../../containers/CustomConfirmationChangesDialog/CustomConfirmationChangesDialog";
import DialogTitle from "../../../../containers/DialogTitle/DialogTitle";
import LoadingOverlay from "../../../../containers/LoadingOverlay/LoadingOverlay";
import { useAppSelector } from "../../../../custom-hooks/redux/redux";
import { isAuthorizedEeGroup } from "../../../../custom-hooks/useAuthorization/useAuthorization";
import { SaveResponse } from "../../../../models/api/save";
import { DocumentDTO } from "../../../../models/documents";
import { EmployeeExpenseReportSTO } from "../../../../models/employee-expense-reports/employee-expense-report-sto";
import { EmployeeRef } from "../../../../models/employees";
import { ExpenseReportDTO } from "../../../../models/expense-reports";
import { ExpenseReportsApprovalFilter } from "../../../../models/expense-reports/expense-reports-approval-filter";
import { TravelExpenseAuditNotesDTO } from "../../../../models/travel-expense-audit-notes";
import AddEditButton from "../../../../reusable-components/AddEditButton/AddEditButton";
import {
  cancelExpenseReports,
  markExpenseReportAsDrafts,
  submitExpenseReports,
} from "../../../../services/employee-expense-reports/employee-expense-reports-api.service";
import {
  approveExpenseReports,
  approveExpenseReportsDocumentation,
  ExpenseReportsApiService,
  markExpenseReportsAsPaid,
  rejectExpenseReports,
  returnExpenseReportsBacktoEmployee,
} from "../../../../services/expense-reports/expense-reports-api.service";
import { AUTHORIZATIONS } from "../../../../utils/constants/authorization-constants";
import { EXPENSE_REPORT_STATUS } from "../../../../utils/constants/constants";
import { ExpenseReportView } from "../utils";
import ExpenseReportContent from "./ExpenseReportContent/ExpenseReportContent";
import { Props, State } from "./interfaces";
import { LegalEntityBlock, MainContainer, StatusAmountBlock, StatusAmountContainer } from "./styles";
import { convertDateToUTC } from "../../../../utils/time-utils";
import { toastifyResponse } from "../../../../utils/toastify-response/toastify-response";

export type ExpenseReportStatusType = (typeof EXPENSE_REPORT_STATUS)[keyof typeof EXPENSE_REPORT_STATUS];

const AddEditExpenseReport = ({
  effect = "add",
  buttonType,
  item,
  handleNotifyParent,
  children,
  view,
  employee,
  readOnly = false,
  parentIsLoading,
  wholeReportIsReadOnly = false,
  hideAuditNotes = false,
}: Props) => {
  const { t } = useTranslation();
  const tRP = "components.add_edit_expense_report";
  const tRPStatus = "expense_report_statuses";

  const { currentUserAuthorizations } = useAppSelector((state) => state.currentUserReducer);

  const isAllowedToApprove = useMemo(() => {
    if (!employee || !employee?.groups?.length) return false;

    return isAuthorizedEeGroup(
      [AUTHORIZATIONS.EmployeesAccess.ApproveExpenses],
      currentUserAuthorizations,
      employee?.groups?.map((group) => group?.id) ?? [],
      true
    );
  }, [currentUserAuthorizations, employee]);

  const isAllowedToApprovePayment = useMemo(() => {
    if (!employee || !employee?.groups?.length) return false;

    return isAuthorizedEeGroup(
      [AUTHORIZATIONS.EmployeesAccess.PayExpenses],
      currentUserAuthorizations,
      employee?.groups?.map((group) => group?.id) ?? [],
      true
    );
  }, [currentUserAuthorizations, employee]);

  const DEFAULT_ITEM: Partial<ExpenseReportDTO> = useMemo(() => {
    return {
      legalEntity: item?.legalEntity ?? employee?.legalEntity ?? null,
      employee: item?.employee ?? employee ?? null,
      date: item?.date ?? new Date(),
      status: item?.status ?? null,
      businessPurposeDescription: item?.businessPurposeDescription ?? "",
      totalAmount: item?.totalAmount ?? 0,
      currency: item?.currency ?? null,
      description: item?.description ?? "",
      preApprovalDocument: item?.preApprovalDocument ?? null,
      clientApproval: item?.clientApproval ?? "",
      documentation: item?.documentation ?? false,
      travelExpenses: item?.travelExpenses ?? [],
      needAcceptance: item?.needAcceptance ?? false,
      acceptedByClient: item?.acceptedByClient ?? false,
      rejectedByClient: item?.rejectedByClient ?? false,
      acceptedRejectedByUser: item?.acceptedRejectedByUser ?? null,
      acceptedRejectedByEmail: item?.acceptedRejectedByEmail ?? "",
      acceptedRejectedAt: item?.acceptedRejectedAt ?? null,
      approvedRejectedAt: item?.approvedRejectedAt ?? null,
      approvedRejectedByUser: item?.approvedRejectedByUser ?? null,
      signatureContents: item?.signatureContents ?? "",
    };
  }, [item]);

  const INITIAL_STATE = useMemo((): State => {
    return {
      isDialogOpen: false,
      loading: false,
      expenseReport: item ?? (DEFAULT_ITEM as ExpenseReportDTO),
      isCCDOpen: false,
      areFieldsValidated: effect === "edit",
      isDocCCCDOpen: false,
      saveAs: null,
      isCCCDOpen: false,
      managerUpdatedStatus: null,
      managerUpdatedDocStatus: false,
      isManagerCCCDOpen: false,
      error: "",
      reloadOnClose: false,
    };
  }, [effect, DEFAULT_ITEM, item]);

  const [state, setState] = useState<State>({
    ...INITIAL_STATE,
  });

  // #region handle change
  const handleChange = useCallback((field: keyof ExpenseReportDTO, value: ExpenseReportDTO[keyof ExpenseReportDTO]) => {
    setState((state) => ({
      ...state,
      expenseReport: {
        ...state.expenseReport,
        [field]: value,
      },
    }));
  }, []);
  //#endregion

  //#region handle discard changes
  const handleDiscardChanges = useCallback(() => {
    const reloadOnClose = state.reloadOnClose;

    setState({ ...INITIAL_STATE });

    reloadOnClose && handleNotifyParent && handleNotifyParent();
  }, [INITIAL_STATE, state.reloadOnClose, handleNotifyParent]);
  //#endregion

  //#region resetAndClose
  const resetAndCloseDialog = (): void => {
    const reportToCompare = {
      ...state.expenseReport,
      travelExpenses: state.expenseReport.travelExpenses.map((el) => {
        return { ...el, auditDocument: null, auditNotes: [] };
      }),
    };

    const initReportToCompare = {
      ...INITIAL_STATE.expenseReport,
      travelExpenses: INITIAL_STATE.expenseReport.travelExpenses.map((el) => {
        return { ...el, auditDocument: null, auditNotes: [] };
      }),
    };

    if (!isEqual(reportToCompare, initReportToCompare)) {
      setState((state) => ({
        ...state,
        isCCDOpen: true,
      }));
    } else {
      handleDiscardChanges();
    }
  };
  //#endregion

  //#region handle sign
  const handleSign = useCallback((signatureContents: string) => {
    setState((state) => ({
      ...state,
      saveAs: EXPENSE_REPORT_STATUS.submitted,
      expenseReport: {
        ...state.expenseReport,
        signatureContents,
      },
    }));
  }, []);
  //#endregion

  //#region handleSave
  const handleSave = useCallback(async () => {
    setState((state) => ({
      ...state,
      loading: true,
    }));

    try {
      let response: SaveResponse | null = null;

      switch (state.saveAs) {
        case EXPENSE_REPORT_STATUS.draft:
          response = await markExpenseReportAsDrafts([
            {
              ...state.expenseReport,
              date: convertDateToUTC(state.expenseReport.date),
              ...(!!state.saveAs && {
                status: state.saveAs,
              }),
              travelExpenses: [
                ...state.expenseReport.travelExpenses.map((te) => ({
                  ...te,
                  date: convertDateToUTC(te.date),
                })),
              ],
              ...(effect === "add" && { id: uuidv4() }),
            } as EmployeeExpenseReportSTO,
          ]);
          break;
        case EXPENSE_REPORT_STATUS.cancelled:
          response = await cancelExpenseReports([
            {
              ...state.expenseReport,
              date: convertDateToUTC(state.expenseReport.date),
              ...(!!state.saveAs && {
                status: state.saveAs,
              }),
              travelExpenses: [
                ...state.expenseReport.travelExpenses.map((te) => ({
                  ...te,
                  date: convertDateToUTC(te.date),
                })),
              ],
              ...(effect === "add" && { id: uuidv4() }),
            } as EmployeeExpenseReportSTO,
          ]);
          break;
        case EXPENSE_REPORT_STATUS.submitted:
          response = await submitExpenseReports([
            {
              ...state.expenseReport,
              date: convertDateToUTC(state.expenseReport.date),
              ...(!!state.saveAs && {
                status: state.saveAs,
              }),
              travelExpenses: [
                ...state.expenseReport.travelExpenses.map((te) => ({
                  ...te,
                  date: convertDateToUTC(te.date),
                })),
              ],
              ...(effect === "add" && { id: uuidv4() }),
            } as EmployeeExpenseReportSTO,
          ]);
      }

      if (response) {
        if (response.error) {
          setState((state) => ({
            ...state,
            error: response ? response.error : "",
          }));
        } else {
          handleDiscardChanges();

          handleNotifyParent && handleNotifyParent();
        }

        toastifyResponse(response);
      }
    } catch (error) {
      setState((state) => ({
        ...state,
        error: error as any,
      }));
    } finally {
      setState((state) => ({
        ...state,
        loading: false,
        isCCCDOpen: false,
        saveAs: null,
      }));
    }
  }, [effect, state.saveAs, state.expenseReport, handleNotifyParent, DEFAULT_ITEM, employee, handleDiscardChanges]);
  //#endregion

  useEffect(() => {
    !!state.saveAs &&
      (!isEqual(state.saveAs.id, EXPENSE_REPORT_STATUS.submitted.id) //When submitting
        ? setState((state) => ({
            ...state,
            isCCCDOpen: true,
          }))
        : handleSave());
  }, [state.saveAs]);

  //#region handle close CCCD
  const handleCloseCCCCD = useCallback(() => {
    setState((state) => ({
      ...state,
      saveAs: null,
      isCCCDOpen: false,
    }));
  }, []);
  //# endregion

  //#region arefiledsValidated
  const areFieldsValidated = useCallback((): boolean => {
    return !!state.expenseReport.legalEntity && !!state.expenseReport.businessPurposeDescription;
  }, [state.expenseReport]);
  //#endregion

  useEffect(() => {
    setState((state) => ({
      ...state,
      areFieldsValidated: areFieldsValidated(),
    }));
  }, [state.expenseReport, areFieldsValidated]);

  useEffect(() => {
    !!item &&
      setState((state) => ({
        ...state,
        expenseReport: item,
      }));

    return () => {
      if (effect === "add") setState({ ...INITIAL_STATE });
    };
  }, [item, effect, INITIAL_STATE]);

  //#region handle notify document change
  const handleNotifyOnDocumentChange = useCallback((document: DocumentDTO) => {
    setState((state) => ({
      ...state,
      expenseReport: {
        ...state.expenseReport,
        preApprovalDocument: document,
      },
    }));
  }, []);
  //#endregion

  //#region unlink document
  const handleUnlinkDocument = useCallback(async () => {
    setState((state) => ({
      ...state,
      expenseReport: {
        ...state.expenseReport,
        preApprovalDocument: null,
      },
      isDocCCCDOpen: false,
    }));
  }, []);
  //#endregion

  //#region close doc CCD
  const handleCloseDocCCCCD = useCallback(() => {
    setState((state) => ({
      ...state,
      isDocCCCDOpen: false,
    }));
  }, []);
  //#endregion

  //#region change status
  const handleChangeStatus = useCallback(
    (newStatus: ExpenseReportStatusType) => {
      if (view === ExpenseReportView.employee) {
        setState((state) => ({
          ...state,
          saveAs: newStatus,
        }));
      }

      if (view === ExpenseReportView.manager) {
        setState((state) => ({
          ...state,
          managerUpdatedStatus: newStatus,
        }));
      }
    },
    [view]
  );
  //#endregion

  //#region close manager CCCCD
  const handleCloseManagerCCCCD = useCallback(() => {
    setState((state) => ({
      ...state,
      managerUpdatedStatus: null,
      managerUpdatedDocStatus: false,
      isManagerCCCDOpen: false,
    }));
  }, []);

  useEffect(() => {
    (!!state.managerUpdatedStatus || state.managerUpdatedDocStatus) &&
      setState((state) => ({
        ...state,
        isManagerCCCDOpen: true,
      }));
  }, [state.managerUpdatedStatus, state.managerUpdatedDocStatus]);
  //#endregion

  //#region savereportas manager
  const handleSaveReportAsManager = useCallback(async () => {
    if (!item) return;

    setState((state) => ({
      ...state,
      loading: true,
    }));

    try {
      let response: SaveResponse | null = null;

      if (state.managerUpdatedStatus) {
        if (isEqual(state.managerUpdatedStatus.id, EXPENSE_REPORT_STATUS.approved.id)) {
          response = await approveExpenseReports({
            employeeId: item.employee?.id,
            expenseReportIDs: [item.id],
          } as ExpenseReportsApprovalFilter);
        } else if (isEqual(state.managerUpdatedStatus.id, EXPENSE_REPORT_STATUS.paid.id)) {
          response = await markExpenseReportsAsPaid({
            employeeId: item.employee?.id,
            expenseReportIDs: [item.id],
          } as ExpenseReportsApprovalFilter);
        } else if (isEqual(state.managerUpdatedStatus, EXPENSE_REPORT_STATUS.rejected)) {
          response = await rejectExpenseReports({
            employeeId: item.employee?.id,
            expenseReportIDs: [item.id],
          } as ExpenseReportsApprovalFilter);
        } else if (isEqual(state.managerUpdatedStatus, EXPENSE_REPORT_STATUS.returned_to_employee)) {
          response = await returnExpenseReportsBacktoEmployee({
            employeeId: item.employee?.id,
            expenseReportIDs: [item.id],
          } as ExpenseReportsApprovalFilter);
        }
      } else if (state.managerUpdatedDocStatus) {
        response = await approveExpenseReportsDocumentation({
          employeeId: item.employee?.id,
          expenseReportIDs: [item.id],
        } as ExpenseReportsApprovalFilter);
      }

      if (response) {
        if (response.error) {
          setState((state) => ({
            ...state,
            error: response?.error ? response.error : "",
          }));
        } else {
          handleDiscardChanges();

          handleNotifyParent && handleNotifyParent();
        }

        toastifyResponse(response);
      }
    } catch (error) {
      setState((state) => ({
        ...state,
        error: error as string,
      }));
    } finally {
      setState((state) => ({
        ...state,
        loading: false,
        isCCCDOpen: false,
        updatedStatus: null,
        updatedDocStatus: false,
      }));
    }
  }, [item, handleNotifyParent, state.managerUpdatedDocStatus, state.managerUpdatedStatus, resetAndCloseDialog]);
  //#endregion

  //#region updateauditactions
  const handleUpdateAuditActions = useCallback(
    (travelExpenseId: string, actualAuditNotes: TravelExpenseAuditNotesDTO[]) => {
      const allTravelExpenses = [...state.expenseReport.travelExpenses];

      const travelExpenseIdx = allTravelExpenses.findIndex((el) => el.id === travelExpenseId);

      if (travelExpenseIdx < 0) return;

      const updatedTravelExpense: TravelExpenseDTO = {
        ...allTravelExpenses[travelExpenseIdx],
        // auditDocument: document,
        auditNotes: actualAuditNotes,
      };

      allTravelExpenses.splice(travelExpenseIdx, 1, updatedTravelExpense);

      setState((state) => ({
        ...state,
        expenseReport: {
          ...state.expenseReport,
          travelExpenses: allTravelExpenses,
        },
        reloadOnClose: true,
      }));
    },
    []
  );
  //#endregion

  //#region change audit mutated status
  const handleChangeAuditMutatedStatus = useCallback(() => {
    setState((state) => ({
      ...state,
      reloadOnClose: true,
    }));
  }, []);
  //#endregion

  //#region handle auditor comment
  const handleAddAuditorComment = useCallback(async () => {
    setState((state) => ({ ...state, loading: true }));
    try {
      const apiService = new ExpenseReportsApiService();

      apiService.useSaveItemsPath("/SaveExpenseReportAuditorComment");
      const response = await apiService.saveItems([state.expenseReport]);
      if (!response.error) {
        handleNotifyParent && handleNotifyParent();
      } else {
        setState((state) => ({ ...state, error: response.error }));
      }
    } catch (error) {
      setState((state) => ({ ...state, error: error as string }));
    } finally {
      setState((state) => ({ ...state, loading: false }));
    }
  }, [state.expenseReport, handleNotifyParent]);
  //#endregion

  //#region handle update export audit notes
  const handleUpdateExportAuditNotes = useCallback((travelExpenseId: string, include: boolean) => {
    console.log(travelExpenseId, include);
    setState((state) => ({
      ...state,
      expenseReport: {
        ...state.expenseReport,
        travelExpenses: [
          ...state.expenseReport.travelExpenses.filter((te) => te.id !== travelExpenseId),
          {
            ...state.expenseReport.travelExpenses.find((te) => te.id === travelExpenseId)!,
            includeNotesInExportedReport: include,
          },
        ],
      },
    }));
  }, []);
  //#endregion

  return (
    <>
      <>
        {!!children && (
          <Grid
            onClick={(event) => {
              // event.preventDefault();
              event?.stopPropagation();
              setState((state) => ({ ...state, isDialogOpen: true }));
            }}
            sx={{
              "&:hover": {
                cursor: "pointer",
              },
            }}
          >
            {children}
          </Grid>
        )}

        {!children && (
          <AddEditButton
            title={t(`buttons.${effect}`)}
            effect={effect}
            asLink={false}
            onClick={() => {
              setState((state) => ({ ...state, isDialogOpen: true }));
            }}
          >
            {effect === "edit" && buttonType === "icon" && <FontAwesomeIcon icon={faEye} />}
          </AddEditButton>
        )}
      </>

      <Dialog
        open={state.isDialogOpen}
        onClose={resetAndCloseDialog}
        fullWidth
        maxWidth="lg"
      >
        <LoadingOverlay loading={state.loading || !!parentIsLoading}>
          <DialogTitle
            title={
              effect === "add"
                ? t(`${tRP}.title_for_add_component`)
                : readOnly
                ? t(`${tRP}.title_for_edit_component`)
                : t(`${tRP}.title`)
            }
            handleClose={resetAndCloseDialog}
            headerActionButtons={
              <>
                {effect === "edit" && (
                  <ExportSingleExpenseReport
                    expenseReport={state.expenseReport}
                    disabled={!!parentIsLoading}
                    view={view}
                    employee={state.expenseReport?.employee as EmployeeRef}
                    hideAuditNotes={hideAuditNotes}
                  />
                )}
              </>
            }
          />
          <DialogContent>
            <MainContainer>
              <>
                {effect === "edit" && (
                  <>
                    <LegalEntityBlock>
                      <Typography
                        variant={"h6"}
                        sx={{ fontWeight: "bold" }}
                      >
                        {`${t("common_words.legal_entity")}`} :
                      </Typography>
                      <Typography variant={"h6"}>{state.expenseReport.legalEntity?.name ?? ""}</Typography>
                    </LegalEntityBlock>
                    <StatusAmountContainer>
                      <StatusAmountBlock>
                        <Typography sx={{ fontWeight: "bold" }}>{`${t(`${tRP}.form.status`)}`} :</Typography>
                        <Typography>
                          {state.expenseReport.status?.name
                            ? `${t(`${tRPStatus}.${state.expenseReport.status.name}`)}`
                            : ""}{" "}
                        </Typography>
                        {state.expenseReport.documentation &&
                        (state.expenseReport.status?.id === EXPENSE_REPORT_STATUS.submitted.id ||
                          state.expenseReport.status?.id === EXPENSE_REPORT_STATUS.returned_to_employee.id) ? (
                          <Typography variant="body2">({`${t(`${tRP}.form.documentation_ok`)}`})</Typography>
                        ) : (
                          ""
                        )}
                        {state.expenseReport.signatureContents && (
                          <img
                            width="100"
                            src={`data:image/svg+xml;base64,${state.expenseReport.signatureContents}`}
                            alt="signature"
                          />
                        )}
                      </StatusAmountBlock>

                      <StatusAmountBlock>
                        <Typography sx={{ fontWeight: "bold" }}>{`${t(`${tRP}.form.total`)}`} :</Typography>
                        <Typography>{state.expenseReport.totalAmount?.toLocaleCustom()}</Typography>
                        <Typography>{state.expenseReport.currency?.code ?? ""}</Typography>
                      </StatusAmountBlock>
                    </StatusAmountContainer>
                  </>
                )}
              </>

              {!!employee && (
                <ExpenseReportContent
                  employee={employee}
                  handleChange={handleChange}
                  expenseReport={state.expenseReport}
                  view={view}
                  handleNotifyOnDocumentChange={handleNotifyOnDocumentChange}
                  handleChangeAuditMutatedStatus={handleChangeAuditMutatedStatus}
                  changeDocCCCDStatus={() => {
                    setState((state) => ({
                      ...state,
                      isDocCCCDOpen: true,
                    }));
                  }}
                  handleNotifyParent={handleNotifyParent}
                  handleUpdateAuditActions={handleUpdateAuditActions}
                  readOnly={
                    readOnly ||
                    view === ExpenseReportView.manager ||
                    (view === ExpenseReportView.employee &&
                      !(
                        state.expenseReport.status?.id === EXPENSE_REPORT_STATUS.draft.id ||
                        state.expenseReport.status?.id === EXPENSE_REPORT_STATUS.returned_to_employee.id ||
                        (effect === "add" && state.expenseReport.status === null)
                      ))
                  }
                  wholeReportIsReadOnly={wholeReportIsReadOnly}
                  hideAuditNotes={hideAuditNotes}
                  handleAddAuditorComment={handleAddAuditorComment}
                  handleUpdateExportAuditNotes={handleUpdateExportAuditNotes}
                />
              )}
            </MainContainer>
          </DialogContent>

          {!wholeReportIsReadOnly && (
            <>
              {view === ExpenseReportView.employee && (
                <>
                  <EmployeeActions
                    employee={employee}
                    effect={effect}
                    resetAndCloseDialog={resetAndCloseDialog}
                    handleSign={handleSign}
                    parentIsLoading={state.loading}
                    expenseReportStatusId={state.expenseReport?.status?.id}
                    handleChangeStatus={handleChangeStatus}
                    disabledButtons={{
                      draft: !state.areFieldsValidated,
                      cancel: !state.areFieldsValidated || !state.expenseReport.businessPurposeDescription,
                      submit: !state.areFieldsValidated || !state.expenseReport.businessPurposeDescription,
                    }}
                  />
                </>
              )}

              {view === ExpenseReportView.manager && (
                <>
                  {(isAllowedToApprove || isAllowedToApprovePayment) && (
                    <>
                      <ManagerActions
                        isAllowedToApprove={isAllowedToApprove}
                        isAllowedToApprovePayment={isAllowedToApprovePayment}
                        expenseReportStatusId={state.expenseReport?.status?.id}
                        handleChangeStatus={handleChangeStatus}
                        documentation={state.expenseReport.documentation}
                        handleChangeDocStatus={(newDocStatus: boolean) => {
                          setState((state) => ({
                            ...state,
                            managerUpdatedDocStatus: newDocStatus,
                          }));
                        }}
                        error={state.error}
                      />
                    </>
                  )}
                </>
              )}
            </>
          )}
        </LoadingOverlay>
      </Dialog>

      <ConfirmationChangesDialog
        open={state.isCCDOpen}
        handleCancel={() => setState({ ...state, isCCDOpen: false })}
        handleDiscard={handleDiscardChanges}
      />

      <CustomConfirmationChangesDialog
        open={state.isCCCDOpen}
        loading={state.loading}
        title={`${t(`${tRP}.form.CCCD_title`)}`}
        handleCancel={handleCloseCCCCD}
        actionsAndHandlers={[
          {
            action: "yes",
            buttonVariant: "contained",
            handler: handleSave,
          },
        ]}
      >
        {state.saveAs
          ? isEqual(state.saveAs.id, EXPENSE_REPORT_STATUS.draft.id)
            ? t(`${tRP}.form.save_as_draft_message`)
            : isEqual(state.saveAs.id, EXPENSE_REPORT_STATUS.submitted.id)
            ? t(`${tRP}.form.submit_message`)
            : isEqual(state.saveAs.id, EXPENSE_REPORT_STATUS.cancelled.id)
            ? t(`${tRP}.form.cancel_message`)
            : ""
          : ""}
      </CustomConfirmationChangesDialog>

      <CustomConfirmationChangesDialog
        open={state.isDocCCCDOpen}
        loading={state.loading}
        title={"Unlink document"}
        handleCancel={handleCloseDocCCCCD}
        actionsAndHandlers={[
          {
            action: "yes",
            buttonVariant: "contained",
            handler: handleUnlinkDocument,
          },
        ]}
      >
        {`${t(`${tRP}.form.unlink_justification_document_message`)}`}
      </CustomConfirmationChangesDialog>

      <ManagerConfirmationDialog
        managerUpdatedStatus={state.managerUpdatedStatus}
        managerUpdatedDocStatus={state.managerUpdatedDocStatus}
        handleSave={handleSaveReportAsManager}
        parentIsLoading={state.loading}
        isCCCDOpen={state.isManagerCCCDOpen}
        handleCloseCCCCD={handleCloseManagerCCCCD}
      />
    </>
  );
};

export default AddEditExpenseReport;
